import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { tokenStorage } from '../lib/tokenStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // XHR helper — uses Authorization header only (no cookies to avoid K8s ingress CORS)
  const xhrRequest = (url, method, body) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url);
      xhr.setRequestHeader('Content-Type', 'application/json');
      const token = tokenStorage.get();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else {
            reject(new Error(data.detail || 'Request failed'));
          }
        } catch (e) {
          reject(new Error('Invalid server response'));
        }
      };
      xhr.onerror = function() {
        reject(new Error('Network error'));
      };
      xhr.send(JSON.stringify(body));
    });
  };

  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      // Get token from localStorage (JWT auth)
      const token = tokenStorage.get();
      
      const response = await fetch(`${API}/auth/me`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      } else {
        setUser(null);
        tokenStorage.remove();
        return null;
      }
    } catch (err) {
      setUser(null);
      tokenStorage.remove();
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    try {
      const data = await xhrRequest(`${API}/auth/login`, 'POST', { email, password });
      tokenStorage.set(data.access_token);
      setUser(data.user);
      return data.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const register = async (name, email, password) => {
    setError(null);
    try {
      const data = await xhrRequest(`${API}/auth/register`, 'POST', { name, email, password, role: 'client' });
      tokenStorage.set(data.access_token);
      setUser(data.user);
      return data.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    try {
      const response = await fetch(`${API}/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'OAuth callback failed');
      }
      
      if (data.access_token) {
        tokenStorage.set(data.access_token);
      }
      
      const userData = data.user || data;
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      const token = tokenStorage.get();
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (err) {
      // Logout endpoint failure is non-critical — token is cleared regardless
    } finally {
      tokenStorage.remove();
      setUser(null);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    loginWithGoogle,
    handleOAuthCallback,
    logout,
    updateUser,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
